import type { ReactNode } from 'react';

import { IconCircle } from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import Price, {
	PriceText,
	usePriceScreenReaderTextGetter,
} from 'components/Price';
import Status, { type StatusVariant } from 'components/Status';
import type { ComparisonPrice, PriceModel } from 'models/price';
import type { HTMLAttributesWithRef, WithRequired } from 'types';
import { cn } from 'utils/classNames';

interface BaseProps extends HTMLAttributesWithRef<
	HTMLAnchorElement | HTMLButtonElement
> {
	comparisonPrice?: ComparisonPrice;
	href?: string;
	image?: ReactNode;
	price?: PriceModel;
	priceLabel?: string;
	size?: 'md' | 'sm';
	statusText?: string;
	statusVariant?: StatusVariant;
	/** Supports line break characters in the string. */
	subtitle?: string;
	title: string;
}
type AnchorProps = WithRequired<BaseProps, 'href'>;
type ButtonProps = WithRequired<BaseProps, 'onClick'>;
type Props = AnchorProps | ButtonProps;

/**
 * Link or button, takes specific props for content parts (instead of
 * children/ReactNode) to construct a readable aria-label.
 */
export default function LinkCard({
	className,
	comparisonPrice,
	href,
	image,
	onClick,
	price,
	priceLabel,
	size = 'md',
	statusText,
	statusVariant = 'positive',
	subtitle,
	title,
	...attrs
}: Props) {
	const getPriceScreenReaderText = usePriceScreenReaderTextGetter();

	// Only using inline elements as children since the container can be
	// a button and block elements in buttons is invalid HTML.
	return (
		<LinkOrButton
			{...attrs}
			href={href}
			onClick={onClick}
			className={cn(
				'group/link-card flex w-full rounded-xs border border-strong px-4 text-left text-sm',
				'hover:border-focus hover:outline-1 hover:outline-focus',
				size === 'sm' && 'items-center py-2',
				size === 'md' && 'py-4',
				className,
			)}
			aria-label={[
				title,
				subtitle
					?.split('\n')
					.map((line) => line.trim())
					.join(', '),
				comparisonPrice &&
					getPriceScreenReaderText({
						label: comparisonPrice.title,
						price: comparisonPrice,
					}),
				statusText,
				price && getPriceScreenReaderText({ label: priceLabel, price }),
			]
				.filter(Boolean)
				.join(', ')}
		>
			{image && (
				<span className="mr-4 max-w-24 shrink-0" aria-hidden="true">
					{image}
				</span>
			)}
			<span className="flex grow flex-col self-center" aria-hidden="true">
				<b className="font-bold">{title}</b>
				{subtitle && <span className="whitespace-pre-line">{subtitle}</span>}
				{comparisonPrice && (
					<PriceText price={comparisonPrice} prefix={comparisonPrice.title} />
				)}
				{statusText && (
					<Status
						className="mt-2"
						size="sm"
						displayType="inline"
						text={statusText}
						variant={statusVariant}
					/>
				)}
				{price && (
					<Price price={price} label={priceLabel} size="xs" className="mt-2" />
				)}
			</span>
			<IconCircle
				name="chevron-right"
				variant="subtle"
				size={size}
				padding={size}
				className="ml-4 self-center group-hover/link-card:bg-subtleHover"
			/>
		</LinkOrButton>
	);
}
LinkCard.displayName = 'LinkCard';
